import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Grid from '@mui/material/Grid'
import { first } from 'rxjs/operators'
import { includes, result } from 'libraries/lodash'
import { shorten } from 'libraries/front-end-core/services/ShortenerService'
import { RESPONSE_PENDING } from 'libraries/front-end-core/constants/responses'
import { scrollTo } from 'libraries/front-end-core/helpers/document'
import { pushUrl } from 'libraries/front-end-core/core/history'
import url from 'libraries/front-end-core/core/url'
import { MW_DEAL_COMPARE } from 'constants/controllers'
import { COMPARISON_DEALS_LIMIT } from 'constants/mw-comparison-limits'
import selectAolName from 'redux/selectors/selectAolName'
import selectDeviceType from 'redux/selectors/selectDeviceType'
import { ALL_SELECTED, DISABLED, EMPTY_SELECTION, NONE_SELECTED, SOME_SELECTED } from 'pages/mwlistdeals/table/Header'
import TableContainer from 'pages/mwlistdeals/table/Body'
import HeaderActionButtons from 'pages/mwlistdeals/HeaderActionButtons'
import Pagination from 'pages/mwlistdeals/table/Footer'
import Modal from 'components/shared/Modal'

const ListDeals = (props) => {
  const {
    selectedDeals,
    setSelectedDeals,
    selectedDealType,
    dealType: _dealType,
    clearSelectedDeals,
    setSignUpModalIsOpen,
    setUserRolesModalIsOpen,
    isLogged,
    hasMarketsAccess,
    deals,
    pageSize,
    pageNumber,
    eventTypes,
    searchPhrase = '',
    pageId
  } = props
  const aol = useSelector(selectAolName)
  const { isMobile } = useSelector(selectDeviceType)
  const [resultStats, setResultStats] = useState()
  const [limitAlertOpen, setLimitAlertOpen] = useState(false)
  const [selected, setSelected] = useState([])
  const [dealType, setDealType] = useState()
  const [select, setSelect] = useState([])
  const [state, setState] = useState()
  const [title, setTitle] = useState()

  useEffect(() => {
    scrollTo({ pageId, id: 0, useWindowScrollTo: isMobile })
  }, [])

  useEffect(() => {
    const selected = deals.results
      .filter(({ dealId }) => includes(selectedDeals, dealId))
      .map(({ dealId }) => dealId)

    setSelected(selected)
  }, [deals.results, selectedDeals])

  useEffect(() => {
    const dealType = selectedDealType || _dealType

    setDealType(dealType)
  }, [selectedDealType, _dealType])

  useEffect(() => {
    const select = deals.results.filter(({ dealId, dealType: dt }) =>
      !includes(selectedDeals, dealId) && dealType === dt
    ).map(({ dealId }) => dealId)

    setSelect(select)
  }, [deals.results, selectedDeals, dealType])

  useEffect(() => {
    const state =
      selected.length === 0 && dealType === 'all'
        ? DISABLED
        : selected.length === 0 && select.length === 0
          ? EMPTY_SELECTION
          : selected.length === 0
            ? NONE_SELECTED
            : select.length > 0 ? SOME_SELECTED : ALL_SELECTED

    setState(state)
  }, [selected, select, dealType])

  useEffect(() => {
    if (!deals.results) {
      setResultStats('')
    }

    const { total } = deals
    if (!total) {
      setResultStats('')
    }

    const lowerBound = pageSize * (pageNumber - 1) + 1
    const upperBound = Math.min(pageSize * pageNumber, total)
    const _resultStats = lowerBound === upperBound
      ? `Showing ${lowerBound} of ${total}`
      : `Showing ${lowerBound}-${upperBound} of ${total}`

    setResultStats(_resultStats)
  }, [deals.results, deals.total, pageSize, pageNumber])

  useEffect(() => {
    const label = result(eventTypes, [_dealType, 'name'], 'All Deals')
    const _resultStats = resultStats ? ` (${resultStats})` : ''

    const title = (searchPhrase ? `Search results - ${label}` : label) + _resultStats

    setTitle(title)
  }, [eventTypes, _dealType, resultStats, searchPhrase])

  const handleCompareSelectedDeals = () => {
    shorten(JSON.stringify({ dealIds: selectedDeals, dealType: selectedDealType })).pipe(
      first(shortened => shortened !== RESPONSE_PENDING)
    ).subscribe(short => pushUrl(url(MW_DEAL_COMPARE, { aol, short })))
  }

  const checkSelectionAbility = (newSelectionLength, callback) => {
    if (selectedDeals.length + newSelectionLength > COMPARISON_DEALS_LIMIT) {
      setLimitAlertOpen(true)
    } else {
      callback()
    }
  }

  const handleSelectAll = () => {
    if (select.length > 0) {
      checkSelectionAbility(select.length, () =>
        setSelectedDeals({ select, dealType })
      )
    } else {
      setSelectedDeals({ deselect: selected, dealType })
    }
  }

  const showDealType = _dealType === 'all'
  const disabled = selectedDeals.length < 2
  const selectedDealName = result(props, [
    'documentConfiguration',
    selectedDealType || _dealType,
    'eventType',
    'displayName'
  ])

  return (
    <>
      <Modal
        open={limitAlertOpen}
        onClose={() => setLimitAlertOpen(false)}
        title='Deal limit reached'
        actions={
          <button
            className='pointer bg-suez-canal br3 ph6 pv4 white f5 bn hover-o-90'
            onClick={() => setLimitAlertOpen(false)}
          >
            Ok
          </button>
      }
      >
        {`You can only select up to ${COMPARISON_DEALS_LIMIT} deals to compare.`}
      </Modal>
      <div className='w-100 layout-width-constraint pt7 pt6-m'>
        <ListDealsHeader
          handleDeselectAll={() => clearSelectedDeals()}
          {...{
            title,
            disabled,
            selectedDeals,
            isLogged,
            hasMarketsAccess,
            setSignUpModalIsOpen,
            setUserRolesModalIsOpen,
            handleCompareSelectedDeals
          }}
        />
        <TableContainer
          {...{ ...props, resultStats, showDealType, state, selectedDealName }}
          checkSelectionAbility={checkSelectionAbility}
          selectedDealType={selectedDealType || _dealType}
          onSelect={handleSelectAll}
        />
        <TablePagination {...props} resultStats={resultStats} />
      </div>
    </>
  )
}

export default ListDeals

const ListDealsHeader = ({
  title,
  selectedDeals,
  handleDeselectAll,
  handleCompareSelectedDeals,
  disabled,
  isLogged,
  hasMarketsAccess,
  setSignUpModalIsOpen,
  setUserRolesModalIsOpen
}) => (
  <div className='bg-dark-sea-green-3 mb4 pb4 ph4'>
    <Grid
      container
      alignItems='center'
      spacing={2}
    >
      <Grid item xs={12} sm={12} md={12} lg={4} className='tc white b'>
        <span data-testid='deals-list-header-title'>{title}</span>
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={8}>
        <HeaderActionButtons
          {...{
            selectedDeals,
            disabled,
            handleDeselectAll,
            handleCompareSelectedDeals,
            isLogged,
            hasMarketsAccess,
            setSignUpModalIsOpen,
            setUserRolesModalIsOpen
          }}
        />
      </Grid>
    </Grid>
  </div>
)

const TablePagination = ({
  deals: { pageNumber, total }, pageSize, dealType, resultStats, parameters, searchPhrase
}) => (
  <Pagination {...{ dealType, pageNumber, pageSize, resultStats, total, parameters, searchPhrase }} />
)
